/*
 * SundayBuilder · Screen 1 · Larger Arcs
 * ---------------------------------------------------------------------
 * Four quiet prose links beneath the Proposition card. Every value
 * comes from the /next-sunday envelope's ``alternatives`` — never
 * fabricated on the client.
 * =================================================================== */

import { useNavigate } from "react-router-dom";

const _MONTHS = ["January", "February", "March", "April", "May", "June",
                 "July", "August", "September", "October", "November", "December"];

function _shortRange(startIso, endIso) {
  if (!startIso || !endIso) return "";
  const [y1, m1, d1] = startIso.split("-").map(Number);
  const [y2, m2, d2] = endIso.split("-").map(Number);
  if (m1 === m2) {
    return `${_MONTHS[m1 - 1]} ${d1}–${d2}`;
  }
  return `${_MONTHS[m1 - 1]} ${d1} – ${_MONTHS[m2 - 1]} ${d2}`;
}

function _monthDay(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${_MONTHS[m - 1]} ${d}`;
}

export default function LargerArcs({ alternatives, onManualDate }) {
  const navigate = useNavigate();
  const nextEight    = alternatives.next_eight_sundays || [];
  const seasonArc    = alternatives.current_or_next_season || null;
  const holyWeek     = alternatives.holy_week || null;
  const annualYear   = alternatives.annual_plan_year || null;

  if (nextEight.length === 0 && !seasonArc && !holyWeek && !annualYear) {
    return null;
  }

  return (
    <>
      <hr className="sb-arcs-divider" />
      <p className="sb-arcs__heading">Or take on a larger arc</p>
      <ul className="sb-arcs__list">
        {nextEight.length > 0 && (
          <li>
            <button
              className="sb-arc"
              onClick={() => {
                // Manual pick — a small inline picker for later.
                // For now, pass the ISO of the first alternative
                // so the flow still routes through onManualDate.
                // (A dedicated 8-Sunday picker is a future refinement.)
                const first = nextEight[0];
                if (first?.iso) onManualDate(first.iso);
              }}
              data-testid="sb-alt-different-sunday"
            >
              <span className="sb-arc__arrow">→</span>
              <span className="sb-arc__label">A different Sunday</span>
              <span className="sb-arc__meta">Pick from the next eight weeks</span>
            </button>
          </li>
        )}
        {seasonArc && (
          <li>
            <button className="sb-arc" data-testid="sb-alt-season" disabled title="Season planning coming soon">
              <span className="sb-arc__arrow">→</span>
              <span className="sb-arc__label">
                {seasonArc.current
                  ? `${seasonArc.name} in progress`
                  : `${seasonArc.name} begins ${_monthDay(seasonArc.starts_iso)}`}
              </span>
              <span className="sb-arc__meta">
                {seasonArc.current ? "Continue planning the season" : "Prepare the season now"}
              </span>
            </button>
          </li>
        )}
        {holyWeek && (
          <li>
            <button className="sb-arc" data-testid="sb-alt-holy-week" disabled title="Holy Week arc coming soon">
              <span className="sb-arc__arrow">→</span>
              <span className="sb-arc__label">Holy Week</span>
              <span className="sb-arc__meta">
                {_shortRange(holyWeek.starts_iso, holyWeek.ends_iso)} · {holyWeek.service_count} services
              </span>
            </button>
          </li>
        )}
        {annualYear && (
          <li>
            <button
              className="sb-arc"
              data-testid="sb-alt-annual"
              onClick={() => navigate(`/annual-planner`)}
            >
              <span className="sb-arc__arrow">→</span>
              <span className="sb-arc__label">Annual Planner™</span>
              <span className="sb-arc__meta">
                Prepare your {annualYear} liturgical year
              </span>
            </button>
          </li>
        )}
      </ul>
    </>
  );
}
